import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AuthService from "@/lib/auth";

// Define types for our access context
type AccessContextType = {
  isVerified: boolean;
  patientId: number;
  accessCode: string;
  expiresAt: Date;
  patientName?: string;
};

export default function QuestionnaireAccess() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const verifyCode = async () => {
      console.log("Verifying code:", code);

      if (!code) {
        console.error("No code provided in URL");
        setError("No access code provided");
        setIsLoading(false);
        return;
      }

      if (!/^[a-zA-Z0-9]{6,8}$/.test(code)) {
        console.error("Invalid code format:", code);
        setError("Invalid access code format");
        setIsLoading(false);
        return;
      }

      try {
        // Use normalized AuthService for verification
        const data = await AuthService.verifyQuestionnaireAccess(code);

        console.log("Access verified:", data);

        if (!data?.patientId) {
          console.error("verifyQuestionnaireAccess returned no patientId:", data);
          setError(data?.error || "Could not load patient for this link. It may have expired. Please contact your clinic.");
          setIsLoading(false);
          return;
        }

        // Prepare access context data
        const accessContext: AccessContextType = {
          isVerified: true,
          patientId: data.patientId,
          accessCode: code,
          expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours from now
          patientName: data.patientName || "Patient",
        };

        // Navigate to the PUBLIC questionnaire info page
        navigate(
          {
            pathname: `/patient-questionnaire-wrapper/${data.patientId}`,
            search: `?access_code=${code}`,
          },
          {
            state: {
              accessVerified: true,
              patientName: accessContext.patientName,
              expiresAt: accessContext.expiresAt.toISOString(),
              patientId: data.patientId,
              accessCode: code,
              // Pass full patient data so wrapper doesn't need a JWT-protected call
              patientData: data.patient || null,
              // Whether patient already submitted responses for this SMS session
              hasExistingResponse: data.hasExistingResponse || false,
            },
            replace: true,
          },
        );
      } catch (err: any) {
        console.error("Verification error:", err);
        setError(err.message || "Invalid or expired access code");
      } finally {
        setIsLoading(false);
      }
    };

    verifyCode();
  }, [code, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-green-500" />
          <p className="text-lg text-gray-600">Verifying your access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-6 max-w-md mx-auto bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Access Denied
          </h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <Button
            onClick={() => {
              window.location.href = "mailto:admin@monitorhealth.ai?subject=Support%20Request%20-%20Monitor%20Health%20AI&body=Hello%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with%20the%20following%20issue:%0D%0A";
            }}
            className="bg-green-500 hover:bg-green-600"
          >
            Contact Support
          </Button>
        </div>
      </div>
    );
  }

  return null; // Will redirect if valid
}
